/**
 * Shared status/severity badge components for the summarization UI.
 *
 * Split out of `shared.ts` (react-refresh/only-export-components) — these are
 * the only component exports the summarization views need, kept separate
 * from the pure formatting/parsing helpers.
 */

import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";

// ---------------------------------------------------------------------------
// Status dot (used in agent detail header)
// ---------------------------------------------------------------------------

export function StatusDot({ status }: { status: string }) {
  switch (status) {
    case "running":
      return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
    case "completed":
      return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
    case "failed":
      return <XCircle className="h-5 w-5 text-destructive" />;
    default:
      return <Clock className="h-5 w-5 text-muted-foreground" />;
  }
}

// ---------------------------------------------------------------------------
// Activity badge — "Processing" / "Queued"
// ---------------------------------------------------------------------------

/**
 * Small text badge that makes an agent's in-flight state unmistakable so a
 * half-finished output can't be read as final (customer feedback: users opened
 * an agent mid-run after a large upload with nothing signalling it was still
 * working). Renders only for the non-final states:
 *
 * - `running`  → "Processing" (the agent is actively analysing now)
 * - `waiting`  → "Queued" (a run is lined up / about to start)
 *
 * `pendingRerun` surfaces the subtler case where a completed agent has a
 * debounced re-run queued (e.g. after a late upload): the status still reads
 * "completed" but a reprocess is coming, so we badge it "Queued" too.
 *
 * Returns `null` for every terminal state with no queued work, so callers can
 * drop it in unconditionally.
 */
export function AgentActivityBadge({
  status,
  pendingRerun,
}: {
  status: string;
  pendingRerun?: boolean;
}) {
  if (status === "running") {
    return (
      <Badge color="blue" variant="soft" size="sm" className="shrink-0 gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing
      </Badge>
    );
  }
  if (status === "waiting" || pendingRerun) {
    return (
      <Badge color="amber" variant="soft" size="sm" className="shrink-0 gap-1">
        <Clock className="h-3 w-3" />
        Queued
      </Badge>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Severity badge
// ---------------------------------------------------------------------------

export function SeverityBadge({ severity }: { severity: string }) {
  const colorMap: Record<string, "red" | "amber" | "blue"> = {
    critical: "red",
    high: "red",
    medium: "amber",
    low: "blue",
  };

  return (
    <Badge
      color={colorMap[severity] ?? "blue"}
      variant="soft"
      size="sm"
      className="mt-0.5 shrink-0"
    >
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </Badge>
  );
}
